/**
 * 이미지 로딩 최적화 유틸리티
 * 여러 이미지를 효율적으로 preload하고 로딩 상태를 관리합니다.
 */

/**
 * 단일 이미지를 preload하는 함수
 * @param {string} src - 이미지 URL
 * @param {function} onLoad - 로드 완료 콜백
 * @param {function} onError - 에러 콜백
 * @returns {Image} Image 객체
 */
export const preloadImage = (src, onLoad, onError) => {
  const img = new Image();
  
  const handleLoad = () => {
    if (onLoad) onLoad(img);
  };
  
  const handleError = (error) => {
    console.error(`이미지 로딩 실패: ${src}`, error);
    if (onError) onError(error, src);
  };
  
  img.onload = handleLoad;
  img.onerror = handleError;
  img.src = src;
  
  return img;
};

/**
 * 여러 이미지를 병렬로 preload하는 클래스
 */
export class ImagePreloader {
  constructor() {
    this.images = new Map();
    this.loadedCount = 0;
    this.totalCount = 0;
    this.isReady = false;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
  }

  /**
   * 이미지 목록을 추가합니다
   * @param {Object} imageMap - {key: url} 형태의 이미지 맵
   */
  addImages(imageMap) {
    Object.entries(imageMap).forEach(([key, src]) => {
      if (src && !this.images.has(key)) {
        this.images.set(key, { src, loaded: false, image: null, error: null });
        this.totalCount++;
      }
    });
  }

  /**
   * 단일 이미지를 추가합니다
   * @param {string} key - 이미지 키
   * @param {string} src - 이미지 URL
   */
  addImage(key, src) {
    if (src && !this.images.has(key)) {
      this.images.set(key, { src, loaded: false, image: null, error: null });
      this.totalCount++;
    }
  }

  /**
   * 모든 이미지를 preload를 시작합니다
   */
  startLoading() {
    if (this.totalCount === 0) {
      this.isReady = true;
      if (this.onComplete) this.onComplete(this.getImageMap());
      return;
    }

    this.images.forEach((imageData, key) => {
      const img = preloadImage(
        imageData.src,
        (loadedImg) => this.handleImageLoad(key, loadedImg),
        (error) => this.handleImageError(key, error)
      );
      imageData.image = img;
    });
  }

  /**
   * 이미지 로드 완료 처리
   */
  handleImageLoad(key, image) {
    const imageData = this.images.get(key);
    if (imageData) {
      imageData.loaded = true;
      imageData.image = image;
      this.loadedCount++;
      
      console.log(`이미지 로드 성공: ${key} (${this.loadedCount}/${this.totalCount})`);
      this.checkProgress();
    }
  }

  /**
   * 이미지 로드 에러 처리
   */
  handleImageError(key, error) {
    const imageData = this.images.get(key);
    if (imageData) {
      imageData.error = error;
      this.loadedCount++; // 에러도 완료로 카운트
      
      console.error(`이미지 로드 실패: ${key} - ${imageData.src}`, error);
      
      if (this.onError) {
        this.onError(error, key, imageData.src);
      }
      
      this.checkProgress();
    }
  }

  /**
   * 진행 상황 확인 및 콜백 호출
   */
  checkProgress() {
    const progress = this.totalCount > 0 ? this.loadedCount / this.totalCount : 1;
    
    if (this.onProgress) {
      this.onProgress(progress, this.loadedCount, this.totalCount);
    }
    
    if (this.loadedCount >= this.totalCount) {
      this.isReady = true;
      if (this.onComplete) {
        this.onComplete(this.getImageMap());
      }
    }
  }

  /**
   * 로드된 이미지들을 맵 형태로 반환
   * @returns {Object} {key: Image} 형태의 이미지 맵
   */
  getImageMap() {
    const imageMap = {};
    this.images.forEach((imageData, key) => {
      imageMap[key] = imageData.loaded ? imageData.image : null;
    });
    return imageMap;
  }

  /**
   * 특정 이미지를 가져옵니다
   * @param {string} key - 이미지 키
   * @returns {Image|null} 이미지 객체 또는 null
   */
  getImage(key) {
    const imageData = this.images.get(key);
    return imageData && imageData.loaded ? imageData.image : null;
  }

  /**
   * 모든 이미지가 로드되었는지 확인
   * @returns {boolean}
   */
  isAllLoaded() {
    return this.isReady;
  }

  /**
   * 로딩 진행률을 반환
   * @returns {number} 0~1 사이의 진행률
   */
  getProgress() {
    return this.totalCount > 0 ? this.loadedCount / this.totalCount : 1;
  }

  /**
   * 에러가 발생한 이미지들을 반환
   * @returns {Array} 에러가 발생한 이미지들의 배열
   */
  getErrors() {
    const errors = [];
    this.images.forEach((imageData, key) => {
      if (imageData.error) {
        errors.push({ key, src: imageData.src, error: imageData.error });
      }
    });
    return errors;
  }

  /**
   * 리소스를 정리합니다
   */
  cleanup() {
    this.images.clear();
    this.loadedCount = 0;
    this.totalCount = 0;
    this.isReady = false;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
  }
}

/**
 * React Hook 스타일의 이미지 preloader
 * @param {Object} imageMap - {key: url} 형태의 이미지 맵
 * @param {Object} options - 옵션 설정
 * @returns {Object} {images, isLoading, progress, errors}
 */
export const useImagePreloader = (imageMap, options = {}) => {
  const {
    onProgress = null,
    onComplete = null,
    onError = null,
    autoStart = true
  } = options;

  // React 환경이 아닌 경우를 위한 간단한 상태 관리
  let preloader = null;
  let images = {};
  let isLoading = true;
  let progress = 0;
  let errors = [];

  const init = () => {
    preloader = new ImagePreloader();
    
    preloader.onProgress = (prog, loaded, total) => {
      progress = prog;
      if (onProgress) onProgress(prog, loaded, total);
    };
    
    preloader.onComplete = (imageMap) => {
      images = imageMap;
      isLoading = false;
      if (onComplete) onComplete(imageMap);
    };
    
    preloader.onError = (error, key, src) => {
      errors.push({ key, src, error });
      if (onError) onError(error, key, src);
    };
    
    preloader.addImages(imageMap);
    
    if (autoStart) {
      preloader.startLoading();
    }
  };

  // 초기화
  if (imageMap && Object.keys(imageMap).length > 0) {
    init();
  } else {
    isLoading = false;
  }

  return {
    images,
    isLoading,
    progress,
    errors,
    preloader,
    startLoading: () => preloader?.startLoading(),
    cleanup: () => preloader?.cleanup()
  };
};

/**
 * 게임 컴포넌트를 위한 이미지 로더 Hook
 * BubbleWrap 스타일의 패턴을 따릅니다
 */
export const useGameImageLoader = (imageUrls, dependencies = []) => {
  let imagesRef = { ready: false };
  let isReady = false;
  
  const loadImages = () => {
    if (!imageUrls || Object.keys(imageUrls).length === 0) {
      imagesRef.ready = true;
      isReady = true;
      return;
    }

    const imageKeys = Object.keys(imageUrls);
    const imageObjects = {};
    let loadedCount = 0;
    const totalCount = imageKeys.length;

    const onLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalCount) {
        imagesRef.ready = true;
        isReady = true;
        // 모든 이미지 객체를 imagesRef에 추가
        Object.assign(imagesRef, imageObjects);
      }
    };

    const onError = (src) => {
      console.error(`이미지 로딩 실패: ${src}`);
      // 에러가 발생해도 계속 진행
      onLoaded();
    };

    imageKeys.forEach(key => {
      const img = new Image();
      img.src = imageUrls[key];
      img.onload = onLoaded;
      img.onerror = () => onError(imageUrls[key]);
      imageObjects[key] = img;
    });
  };

  // 의존성이 변경되면 이미지 로딩 재시작
  const currentDeps = JSON.stringify(dependencies);
  const prevDeps = currentDeps; // 실제 React 환경에서는 useRef 등을 사용

  if (currentDeps !== prevDeps) {
    loadImages();
  }

  return {
    imagesRef,
    isReady
  };
};

export default {
  preloadImage,
  ImagePreloader,
  useImagePreloader,
  useGameImageLoader
};
